// src/composables/useWhiteTheme.js
import { computed, nextTick, ref } from 'vue';

export function useWhiteTheme() {
  // Map of white shades to their CSS variables
  const whiteShades = {
    pure: 'var(--white-pure)', // #FFFFFF - Pure white
    pearl: 'var(--white-pearl)', // #FCFCFC - Pearl white
    soft: 'var(--white-soft)', // #F8F8F8 - Soft white
    light: 'var(--white-light)', // #F5F5F5 - Light white
    gray: 'var(--white-gray)', // #EEEEEE - Gray white
  };

  // Use a ref to track current shade
  const currentShade = ref('pure'); // Default fallback

  // Get the current white shade
  const currentWhiteShade = computed(() => {
    return currentShade.value;
  });

  // Set the white shade
  const setWhiteShade = async (shade) => {
    try {
      // Check if the shade is valid
      if (!whiteShades[shade]) {
        console.warn(`Invalid white shade: ${shade}`);
        return;
      }

      // Update our local ref immediately
      currentShade.value = shade;

      // Wait for next tick to ensure Vue is ready
      await nextTick();

      // Try to update the store if available
      try {
        const { useStore } = await import('vuex');
        const store = useStore();

        if (store && store.state && store.state.theme) {
          store.commit('theme/SET_WHITE_SHADE', shade);
        }
      } catch (storeError) {
        // Store not available, that's okay - we're using local state
        console.debug('Store not available for theme update, using local state only');
      }
    } catch (error) {
      console.warn('Failed to set white shade:', error);
    }
  };

  // Get the current white shade as a CSS class
  const currentBackgroundClass = computed(() => `bg-white-${currentWhiteShade.value}`);

  return {
    // Current state
    currentWhiteShade,
    currentBackgroundClass,

    // Available options
    whiteShades,

    // Actions
    setWhiteShade,
  };
}
